"use client";

import { motion, useScroll, useTransform } from "framer-motion";
import dynamic from "next/dynamic";
import Image from "next/image";
import { useLocale, useTranslations } from "next-intl";
import React from "react";

import AnimatedWarrantyCar from "./AnimatedWarrantyCar";
import PublicBannerCreative from "@/components/banners/PublicBannerCreative";
import RotatingBanner from "@/components/banners/RotatingBanner";
import IconCheck from "@/components/Icons/IconCheck";
import UiButton from "@/components/ui/buttons/Button";
import { usePublicBanner } from "@/hooks/banners/usePublicBanner";
import { Link } from "@/i18n/routing";
import { BANNER_SLOTS } from "@/lib/banners/constants";
import { PublicBannerItem } from "@/lib/banners/types";
import TradeinImageAr from "@/public/assets/images/trade-in-ar.png";
import TradeinImage from "@/public/assets/images/trade-in.png";
// import TradeinImage from "@/public/assets/images/eid-sale-home.webp";
import { DEFAULT_PLACEHOLDER } from "@/utils/constants";

const EscapeRoomOfferModal = dynamic(() => import("@/components/banners/EscapeRoomOfferModal"));

type TradeInProps = {
  banner?: PublicBannerItem | null;
  /** Banner button opens the offer popup. Pass false to follow the CTA link again. */
  ctaOpensOffer?: boolean;
};

const TradeIn: React.FC<TradeInProps> = ({ banner = null, ctaOpensOffer = true }) => {
  const t = useTranslations("Home.tradeIn");
  const j = useTranslations("Common");
  const locale = useLocale();
  const isRTL = locale === "ar";

  const { banners: apiBanners } = usePublicBanner({
    slotKey: BANNER_SLOTS.homePageBanner,
    locale,
  });
  const banners = apiBanners.length ? apiBanners : banner ? [banner] : [];

  const [offerOpen, setOfferOpen] = React.useState(false);
  const openOffer = () => setOfferOpen(true);

  const ref = React.useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });

  // RTL: slides from right (40%), LTR: slides from left (-40%)
  const leftColumnTranslateX = useTransform(
    scrollYProgress,
    [0, 0.35, 0.5, 1],
    isRTL ? ["40%", "0%", "0%", "0"] : ["-40%", "0%", "0%", "0"],
  );

  return (
    <section ref={ref} className="my-2 xl:my-28">
      <div className="grid grid-cols-1 items-stretch gap-4 xl:h-full xl:grid-cols-6 xxl:grid-cols-4">
        <motion.div
          style={{ translateX: leftColumnTranslateX }}
          className="relative hidden w-full items-center overflow-hidden rounded-3xl lg:block xl:col-span-2 xxl:col-span-1"
        >
          {banners.length ? (
            <RotatingBanner banners={banners} className="h-full w-full" transition="slide">
              {(activeBanner, ctaBanner, meta) => (
                <PublicBannerCreative
                  banner={activeBanner}
                  ctaBanner={ctaBanner}
                  withKenBurns={meta?.withKenBurns}
                  // imageHref="/buy-used-cars-uae"
                  imageClassName="absolute z-10 h-full w-full rounded-3xl"
                  priority
                  quality={75}
                  sizes="(max-width: 1280px) 100vw, 1280px"
                  isBanner
                  alt={activeBanner.bannerName || "Trade In"}
                  ctaLabelFallback={j("checkYourEligibility")}
                  ctaHrefFallback="/used-car-finance"
                  ctaClassName="w-11/12 justify-center rounded-2xl font-semibold"
                  onCtaClick={ctaOpensOffer ? openOffer : undefined}
                />
              )}
            </RotatingBanner>
          ) : (
            <>
              <Link href="/buy-used-cars-uae">
                <Image
                  src={isRTL ? TradeinImageAr : TradeinImage}
                  className="absolute z-10 h-full w-full rounded-3xl"
                  priority
                  placeholder="blur"
                  blurDataURL={DEFAULT_PLACEHOLDER}
                  width={600}
                  height={100}
                  quality={100}
                  alt="Trade In"
                  sizes="(max-width: 1280px) 100vw, 1280px"
                />
              </Link>

              <div className="absolute bottom-3 z-20 flex w-full items-center justify-center">
                {ctaOpensOffer ? (
                  <UiButton
                    type="button"
                    onClick={openOffer}
                    buttonText={j("checkYourEligibility")}
                    bgColor="white"
                    textColor="black"
                    className="!rounded-2xl px-4 py-2 font-urbanist text-xl font-semibold hover:bg-white/90 xl:w-11/12"
                  />
                ) : (
                  <Link
                    href="/used-car-finance"
                    className="!rounded-2xl bg-white text-center text-base font-semibold text-black hover:bg-white/90 hover:text-black xl:w-11/12"
                  >
                    <UiButton
                      buttonText={j("checkYourEligibility")}
                      className="px-4 py-2 font-urbanist text-xl font-semibold"
                    />
                  </Link>
                )}
              </div>
            </>
          )}
        </motion.div>

        <div className="flex flex-col gap-4 xl:col-span-4 xl:h-full xxl:col-span-3">
          <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
            <div className="relative flex max-w-full flex-col items-center justify-between rounded-4xl bg-[#fff] pb-9">
              <div className="absolute top-6 mx-auto flex flex-wrap justify-center gap-2 xxxl:max-w-xl xxxl:px-6">
                <div className="inter flex items-center space-x-2 space-x-reverse rounded-full bg-[#EDF1F5] px-4 py-2 font-medium text-black">
                  <IconCheck />
                  <span className="text-xs md:text-sm">{t("inspection")}</span>
                </div>
                <div className="inter flex items-center space-x-2 space-x-reverse rounded-full bg-[#EDF1F5] px-4 py-2 font-medium text-black">
                  <IconCheck />
                  <span className="text-xs md:text-sm">{t("mileageVerified")}</span>
                </div>
                <div className="inter flex items-center space-x-2 space-x-reverse rounded-full bg-[#EDF1F5] px-4 py-2 font-medium text-black">
                  <IconCheck />
                  <span className="text-xs md:text-sm">{t("rtaCertified")}</span>
                </div>
              </div>

              <div className="pt-2">
                <div className="relative h-[250px] w-[300px] overflow-hidden ixs:w-[350px] xl:h-[300px] 2xl:w-[450px]">
                  <Image
                    src="/assets/images/car-1.png"
                    alt="Car 1"
                    width={437}
                    height={350}
                    className="absolute top-12 h-full object-contain xl:top-10 2xl:top-12"
                  />
                </div>

                <p className="urbanist mx-auto max-w-xs text-center text-xl font-medium">{t("qualityMessage")}</p>
              </div>
            </div>
            <div className="relative rounded-4xl bg-[#fff]">
              <AnimatedWarrantyCar
                carBodySrc="/assets/images/car-body.png"
                frontWheelSrc="/assets/images/wheel-front.png"
                rearWheelSrc="/assets/images/wheel-back.png"
                wheelPositions={{
                  desktop: { front: { x: 101, y: 189 }, rear: { x: 302, y: 186 } },
                  mobile: { front: { x: 79, y: 181 }, rear: { x: 235, y: 179 } },
                }}
              />
            </div>
          </div>

          <div className="flex flex-grow flex-col items-center justify-start rounded-4xl bg-[#fff] px-6 lg:flex-row">
            <div className="relative order-6 h-48 w-64 lg:order-first">
              <Image src="/assets/images/approval.svg" alt="finance" width={256} height={192} className="absolute" />
            </div>
            <div className="ml-6 mt-8 flex flex-col justify-center space-y-3 lg:ml-6 lg:mt-0 lg:space-y-3 lg:text-left">
              <p className="flex items-center text-center font-urbanist text-xl font-medium text-primary-foreground lg:text-left">
                {t("financeTitle")}{" "}
                <span className="mx-2 flex items-center justify-center rounded-full bg-black px-4 py-1 text-white">
                  0%
                </span>
              </p>
              <p className="text-center font-inter text-[#292D34CC] lg:text-left">{t("downpayment")}</p>
            </div>
          </div>
        </div>
      </div>
      {ctaOpensOffer ? <EscapeRoomOfferModal isOpen={offerOpen} onClose={() => setOfferOpen(false)} /> : null}
    </section>
  );
};

export default TradeIn;
